import {
    Box,
    Button,
    DynamicDatagrid,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
    Paper,
    Refresh,
} from '@leap4/frontend';
import { useContext, useCallback } from 'react';
import { ReportContext } from '../reportContext';
import type { IReportingMetadata } from '../hooks/useReportingMetadata';
import { formatNumericDisplay, formatTimestampUtc, predictionErrorBand } from '../reports';

interface ReportTableProps {
    data: any[];
    columns: any[];
    loading?: boolean;
    refreshFunc?: () => void;
}

const formatToTwoDecimals = (value: any): string => {
    const actualValue = value?.value !== undefined ? value.value : value;
    if (actualValue == null) return '';
    const num = typeof actualValue === 'number' ? actualValue : parseFloat(actualValue);
    if (isNaN(num)) return String(actualValue);
    return num.toFixed(2);
};

// late_conversion_count is fractional credit, so it arrives as a long float; the other late
// columns are whole counts and need no formatter.
const twoDecimalColumns = new Set(['c4', 'c5', 'late_conversion_count']);

const ReportTableToolbar = (props: { refresh?: () => void }) => (
    <GridToolbarContainer sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5 }}>
        <GridToolbarQuickFilter
            sx={{
                flex: 1,
                maxWidth: 300,
                '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                },
            }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            <GridToolbarColumnsButton slotProps={{ button: { size: 'small' } }} />
            <GridToolbarFilterButton slotProps={{ button: { size: 'small' } }} />
            <GridToolbarDensitySelector slotProps={{ button: { size: 'small' } }} />
            {props.refresh && (
                <Button onClick={props.refresh} startIcon={<Refresh />} size="small">
                    <b>RELOAD</b>
                </Button>
            )}
        </Box>
    </GridToolbarContainer>
);

const getActualValue = (value: any): any => (value?.value !== undefined ? value.value : value);

const TOTALS_ROW_ID = '__totals__';
const TOTALS_LABEL = 'OVERALL';

const convertToDataGridColumns = (
    columns: any[],
    metadata: IReportingMetadata,
): GridColDef[] => {

    const descriptions = new Map(
        metadata.metrics.filter(m => m.description).map(m => [m.key, m.description as string]),
    );
    const timestampKeys = new Set(
        metadata.dimensions.filter(d => d.isTimestamp).map(d => d.key),
    );

    return columns.map(col => {
        const colDef: GridColDef = {
            field: col.name,
            headerName: col.label || col.name,
            flex: 1,
            minWidth: 100,
            sortable: col.options?.sort !== false,
            filterable: col.options?.filter !== false,
            description: descriptions.get(col.name),
        };

        if (twoDecimalColumns.has(col.name)) {
            colDef.valueFormatter = formatToTwoDecimals;
        }

        if (col.format) {
            colDef.valueFormatter = (value: any) =>
                formatNumericDisplay(getActualValue(value), col.format);
        }

        // Bands rather than a bare number: an error percentage is only ever read as "can I use this",
        // and the thresholds are a business call the reader should not have to remember.
        if (col.isErrorPct) {
            colDef.valueFormatter = (value: any) => {
                const actual = getActualValue(value);
                if (actual === TOTALS_LABEL) return TOTALS_LABEL;
                const pct = typeof actual === 'number' ? actual : Number.parseFloat(String(actual ?? ''));
                return Number.isFinite(pct) ? `±${pct.toFixed(1)}%` : '';
            };
            colDef.cellClassName = (params: any) => {
                const band = predictionErrorBand(getActualValue(params.value));
                return band ? `pred-error-${band}` : '';
            };
        }

        if (timestampKeys.has(col.name)) {
            colDef.minWidth = 200;
            colDef.valueFormatter = (value: any) => formatTimestampUtc(getActualValue(value));
        }

        const baseFormatter = colDef.valueFormatter;
        if (baseFormatter) {
            colDef.valueFormatter = ((value: any, ...rest: any[]) =>
                getActualValue(value) === TOTALS_LABEL
                    ? TOTALS_LABEL
                    : (baseFormatter as any)(value, ...rest)) as any;
        }

        return colDef;
    });
};

export const ReportTable = (props: ReportTableProps) => {
    const { data, columns, loading = false, refreshFunc } = props;
    const {
        totalCount,
        totals,
        paginationModel,
        onPaginationModelChange,
        sortModel,
        onSortModelChange,
        filterModel,
        onFilterModelChange,
        metadata,
    } = useContext(ReportContext);

    const gridColumns = convertToDataGridColumns(columns, metadata);

    const rows = data.map((row, index) => ({
        id: `${row.pixel_date || ''}-${row.demand_id || ''}-${row.supply_id || ''}-${index}`,
        ...row,
    }));

    let pinnedRows: { bottom: any[] } | undefined;
    if (totals && rows.length > 0) {
        const labelField = gridColumns.find(col => totals[col.field] === undefined)?.field;
        pinnedRows = {
            bottom: [{
                id: TOTALS_ROW_ID,
                ...totals,
                ...(labelField ? { [labelField]: TOTALS_LABEL } : {}),
            }],
        };
    }

    const handlePageChange = useCallback((newPage: number) => {
        onPaginationModelChange({ page: newPage, pageSize: paginationModel.pageSize });
    }, [onPaginationModelChange, paginationModel.pageSize]);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        onPaginationModelChange({ page: 0, pageSize: newPageSize });
    }, [onPaginationModelChange]);

    return (
        <Paper sx={{ margin: '10px 0', borderRadius: '16px', boxShadow: 'none' }} elevation={5}>
            <DynamicDatagrid
                key={gridColumns.map(col => col.field).join('|')}
                rows={rows}
                columns={gridColumns as any}
                loading={loading}
                loadError={false}
                refreshFunc={refreshFunc}
                toolbar={ReportTableToolbar}
                toolbarProps={{ refresh: () => refreshFunc?.() }}
                pageSize={paginationModel.pageSize}
                style={{ minHeight: '75vh', width: '100%' }}
                pinnedRows={pinnedRows}
                rootSx={{
                    '& .MuiDataGrid-pinnedRows .MuiDataGrid-cell': {
                        fontWeight: 600,
                    },
                    '& .pred-error-good': { color: '#1b7f3b', fontWeight: 600 },
                    '& .pred-error-warn': { color: '#b26a00', fontWeight: 600 },
                    '& .pred-error-bad': { color: '#c62828', fontWeight: 600 },
                }}
                paginationMode="server"
                rowCount={totalCount}

                // Fully controlled pagination, overriding the grid's internal model: it can never
                // desync from the page actually fetched.
                paginationModel={{ page: paginationModel.page, pageSize: paginationModel.pageSize }}
                onPaginationModelChange={(model: { page: number; pageSize: number }) => {
                    if (model.pageSize !== paginationModel.pageSize) {
                        handlePageSizeChange(model.pageSize);
                    } else if (model.page !== paginationModel.page) {
                        handlePageChange(model.page);
                    }
                }}
                rowsPerPageOptions={[25, 50, 100, 250]}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={onFilterModelChange}
            />
        </Paper>
    );
};
